"use client";

import { toast } from 'sonner';

// ─── Shared base styles ───────────────────────────────────────────────────────

const base = {
  fontFamily: 'IBM Plex Mono, monospace',
  fontSize: '12px',
  borderRadius: '10px',
  backdropFilter: 'blur(12px)',
};

// ─── useAppToast ──────────────────────────────────────────────────────────────
// Provides success(), error(), and alert() toast helpers with
// cybersecurity-themed styling consistent with the platform design.

export function useAppToast() {
  /**
   * Success toast — data exports, settings saved, operations completed
   */
  const success = (title: string, description?: string) => {
    toast.success(title, {
      description,
      duration: 5000,
      style: {
        ...base,
        background: 'rgba(10, 20, 14, 0.92)',
        border: '1px solid rgba(0, 255, 136, 0.30)',
        color: '#E8EDF5',
        boxShadow: '0 0 14px rgba(0, 255, 136, 0.18)',
      },
      classNames: {
        title: 'text-[#00FF88] font-semibold',
        description: 'text-[#8892A4]',
        icon: 'text-[#00FF88]',
      },
    });
  };

  /**
   * Error toast — API failures, connection errors, operation failures
   */
  const error = (title: string, description?: string) => {
    toast.error(title, {
      description,
      duration: 8000,
      style: {
        ...base,
        background: 'rgba(20, 8, 8, 0.92)',
        border: '1px solid rgba(255, 59, 59, 0.40)',
        color: '#E8EDF5',
        boxShadow: '0 0 14px rgba(255, 59, 59, 0.22)',
      },
      classNames: {
        title: 'text-[#FF4D4D] font-semibold',
        description: 'text-[#8892A4]',
        icon: 'text-[#FF4D4D]',
      },
    });
  };

  /**
   * Alert toast — threat detected, security warnings, policy violations
   */
  const alert = (title: string, description?: string) => {
    toast.warning(title, {
      description,
      duration: 10000,
      style: {
        ...base,
        background: 'rgba(20, 14, 4, 0.92)',
        border: '1px solid rgba(255, 140, 0, 0.40)',
        color: '#E8EDF5',
        boxShadow: '0 0 14px rgba(255, 140, 0, 0.22)',
      },
      classNames: {
        title: 'text-[#FF8C00] font-semibold',
        description: 'text-[#8892A4]',
        icon: 'text-[#FF8C00]',
      },
    });
  };

  /**
   * Info toast — general informational messages
   */
  const info = (title: string, description?: string) => {
    toast.info(title, {
      description,
      duration: 5000,
      style: {
        ...base,
        background: 'rgba(8, 12, 24, 0.92)',
        border: '1px solid rgba(0, 136, 255, 0.30)',
        color: '#E8EDF5',
        boxShadow: '0 0 12px rgba(0, 136, 255, 0.16)',
      },
      classNames: {
        title: 'text-[#0088FF] font-semibold',
        description: 'text-[#8892A4]',
        icon: 'text-[#0088FF]',
      },
    });
  };

  return { success, error, alert, info };
}
